import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  formatNumberShort,
  formatTooltipValue,
  ECHARTS_OPTS,
  FALLBACK_PALETTE,
} from './chartConfig';

export const BulletChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);
    const palette = colors && colors.length > 0 ? colors : FALLBACK_PALETTE;

    const categories = data.map(d => String(d.name ?? ''));
    const actuals = data.map(d => Number(d.value ?? d.actual ?? 0));
    const targets = data.map(d => Number(d.target ?? 0));
    const ranges = data.map(d => {
      if (Array.isArray(d.ranges)) return d.ranges.map(Number);
      const max = Number(d.max ?? d.target ?? d.value ?? 100) * 1.2;
      return [max * 0.5, max * 0.75, max];
    });

    const globalMax = Math.max(
      ...actuals,
      ...targets,
      ...ranges.flat(),
    ) * 1.1;

    const series: any[] = [];

    // Range bands (poor / satisfactory / good) as stacked bars
    const rangeColors = ['#EDE6E1', '#D9CFC4', '#A8989F'];
    const numBands = Math.max(...ranges.map(r => r.length));
    for (let band = numBands - 1; band >= 0; band--) {
      series.push({
        type: 'bar',
        stack: 'range',
        data: ranges.map(r => (r[band] ?? 0) - (band > 0 ? (r[band - 1] ?? 0) : 0)),
        barWidth: '60%',
        itemStyle: { color: rangeColors[band % rangeColors.length], borderRadius: 0 },
        silent: true,
        z: 1,
      });
    }

    // Actual value bar
    series.push({
      type: 'bar',
      data: actuals,
      barWidth: '30%',
      itemStyle: { color: palette[0], borderRadius: [0, 3, 3, 0] },
      z: 2,
      label: {
        show: data.length <= 10,
        position: 'right' as const,
        fontSize: 11,
        formatter: (p: { value: number }) => formatNumberShort(p.value),
      },
    });

    // Target marker line
    series.push({
      type: 'scatter',
      data: targets.map((t, i) => [t, i]),
      symbol: 'rect',
      symbolSize: [3, 20],
      itemStyle: { color: '#1F1A20' },
      z: 3,
    });

    return {
      ...base,
      grid: { top: 16, right: 40, bottom: 24, left: 16, containLabel: true },
      xAxis: {
        type: 'value',
        max: globalMax,
        axisLabel: { fontSize: 11, color: '#7B6B72', formatter: (v: number) => formatNumberShort(v) },
        splitLine: { lineStyle: { color: '#EDE5D8', type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: { fontSize: 11, color: '#7B6B72' },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const idx = (items[0] as any)?.dataIndex ?? 0;
          return `<div style="font-size:12px"><b>${categories[idx]}</b><br/>Actual: ${formatTooltipValue(actuals[idx])}<br/>Target: ${formatTooltipValue(targets[idx])}</div>`;
        },
      },
      legend: { show: false },
      series,
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
